import React, { useState } from 'react'
import { Box, Grid } from '@material-ui/core'
import { FormContext, useForm } from 'react-hook-form'

import { Container, Page } from 'components'
import {
  AcceptanceForm,
  Header,
  ReportVersionTable,
  ExportFormatDialog,
} from './components'
import AcceptanceReportPdf from './components/AcceptanceReportPdf'
import schema from './components/AcceptanceForm/schema'

import useSnackbar from 'hooks/useSnackbar'

import * as service from 'service'
import helpers from 'helpers'
import constants from 'constants/index'

import useStyles from './styles'

const AcceptanceReport = () => {
  const [currentReportVersion, setCurrentReportVersion] = useState(null)
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false)
  const [isExportingPdf, setIsExportingPdf] = useState(false)
  const [isExportingExcel, setIsExportingExcel] = useState(false)
  const [loadingStats, setLoadingStats] = useState(false)
  const [refresher, setRefresher] = useState(false)
  const [consentFormId, setConsentFormId] = useState(null)

  const snackbar = useSnackbar()
  const classes = useStyles()

  const formMethods = useForm({
    validationSchema: schema({
      onlyReportVersion: false,
      hasCurrentVersion: !!currentReportVersion,
    }),
    defaultValues: {
      documentId: '',
      documentVersionId: null,
      exportFormat: '',
      dataAction: constants.reportVersions.DESTROY_VERSION_ID_ACTION,
    },
  })

  const { watch, triggerValidation, handleSubmit } = formMethods

  const data = watch()

  const { EXPORT_FORMAT_TYPES } = constants.reports

  const handleRefresh = () => setRefresher((prevState) => !prevState)
  const handleCloseExportDialog = () => setIsExportDialogOpen(false)

  const buttonAction = async () => {
    const valid = await triggerValidation()
    if (!valid) return

    setIsExportDialogOpen(true)
  }

  const handleSelectExcelExport = (data) => {
    setIsExportingExcel(true)
    try {
      const excelParams = {
        shouldCreateVersion:
          data.dataAction !== constants.reportVersions.DOWNLOAD_ONLY_ID_ACTION,
        ...data,
      }

      service.dponet.consent.consentFormAcceptanceExportReport({
        consentFormId,
        ...excelParams,
      })

      snackbar.open({
        message: 'O relatório Excel será gerado e enviado por e-mail!',
        variant: 'success',
      })
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(
          error?.response?.data?.error || 'Falha ao exportar relatório Excel.',
        ),
        variant: 'error',
      })
    } finally {
      setIsExportingExcel(false)
    }
  }

  const onConfirmExport = async (data) => {
    handleCloseExportDialog()
    if (
      data.dataAction === constants.reportVersions.DESTROY_VERSION_ID_ACTION
    ) {
      try {
        await service.dponet.reportVersions.destroy({
          reportVersionId: currentReportVersion?.id,
        })
        setCurrentReportVersion()
      } catch (error) {
        snackbar.open({
          message: helpers.formatters.errorMessage(
            error?.response?.data?.error ||
              'Falha ao remover versão do relatório.',
          ),
          variant: 'error',
        })
        return
      }
    }

    const consentFormResponse = await service.dponet.privacyPolicies.get({
      id: data?.documentId,
    })
    const documents = consentFormResponse?.data?.privacyPolicies
    const document = documents[0]

    if (data.exportFormat === EXPORT_FORMAT_TYPES.PDF) {
      setConsentFormId(document?.consentFormId)
      setIsExportingPdf(true)
    } else {
      handleSelectExcelExport({
        ...data,
        consentFormId: document?.consentFormId,
      })
    }

    setTimeout(handleRefresh, 8000)
  }

  return (
    <FormContext {...formMethods}>
      <Page className={classes.root} title="Relatório de Aceites">
        <Container maxWidth={false} spacingXl={15} spacingLg={10} spacingMd={5}>
          <Box py={5}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Header
                  type="acceptance"
                  reportTour="acceptance"
                  isLoading={loadingStats}
                  disabled={isExportingPdf || isExportingExcel}
                  buttonAction={buttonAction}
                />
              </Grid>
              <Grid item xs={12}>
                <AcceptanceForm setLoadingStats={setLoadingStats} />
              </Grid>
              <Grid item xs={12}>
                <ReportVersionTable
                  currentReportVersion={currentReportVersion}
                  kind={constants.reportVersions.KIND.ACCEPTANCE}
                  refresher={refresher}
                  setCurrentReportVersion={setCurrentReportVersion}
                />
              </Grid>
            </Grid>
          </Box>
          <AcceptanceReportPdf
            exportingPdf={isExportingPdf}
            setExportingPdf={setIsExportingPdf}
            data={data}
            shouldCreateVersion={!currentReportVersion?.id}
            refresh={handleRefresh}
            consentFormId={consentFormId}
          />
          <ExportFormatDialog
            open={isExportDialogOpen}
            onClose={handleCloseExportDialog}
            onConfirmExport={handleSubmit(onConfirmExport)}
            isLoadingExcel={isExportingExcel}
            currentReportVersion={currentReportVersion}
          />
        </Container>
      </Page>
    </FormContext>
  )
}

export default AcceptanceReport
